import { createSlice } from '@reduxjs/toolkit'
import {
  createSpecificationAction,
  fetchSpecificationsAction,
  updateOnboardingAction,
  updateSpecificationAction,
} from './specificationAction'
import { SpecificationState } from '../../types/specification'
import { logout } from '../keycloak/keycloakSlice'
import { initializeKeycloak } from '../keycloak/login'

const initialState: SpecificationState = {
  specificationData: null,
  loading: true, // start true so routing waits for the first fetch before deciding
  dataSpecifications: [],
  error: null,
}

const clearSpecs = (state: SpecificationState) => {
  state.specificationData = null
  state.dataSpecifications = []
  state.loading = true
  state.error = null
}

const specificationSlice = createSlice({
  name: 'specification',
  initialState,
  reducers: {
    resetSpecifications: clearSpecs,
  },
  extraReducers: (builder) => {
    builder
      // Clear stale settings as soon as a new login starts so App routing
      // never decides from a previous session's NormBase.
      .addCase(initializeKeycloak.pending, clearSpecs)
      .addCase(logout, clearSpecs)
      .addCase(createSpecificationAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createSpecificationAction.fulfilled, (state, action) => {
        state.loading = false
        state.specificationData = action.payload
        state.dataSpecifications = [action.payload]
      })
      .addCase(createSpecificationAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'An error occurred'
      })
      .addCase(fetchSpecificationsAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSpecificationsAction.fulfilled, (state, action) => {
        state.loading = false
        const payload = action.payload as unknown
        const specifications = Array.isArray(payload)
          ? payload
          : Object.keys((payload as object) || {})
              .filter((key) => !isNaN(Number(key)))
              .map((key) => (payload as Record<string, unknown>)[key])
        state.dataSpecifications = specifications as typeof state.dataSpecifications
      })

      .addCase(fetchSpecificationsAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'An error occurred'
      })
      .addCase(updateSpecificationAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateSpecificationAction.fulfilled, (state, action) => {
        state.loading = false
        const updatedSpec = action.payload
        const index = state.dataSpecifications.findIndex(
          (spec) => spec.SpecificationId === updatedSpec.SpecificationId
        )
        if (index !== -1) {
          state.dataSpecifications[index] = {
            ...updatedSpec,
            Onboarding:
              updatedSpec.Onboarding ??
              state.dataSpecifications[index].Onboarding,
          }
        } else {
          state.dataSpecifications.push(updatedSpec)
        }
      })
      .addCase(updateSpecificationAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'An error occurred'
      })
      .addCase(updateOnboardingAction.fulfilled, (state, action) => {
        const updatedSpec = action.payload
        const index = state.dataSpecifications.findIndex(
          (spec) =>
            spec._id === updatedSpec._id ||
            spec.SpecificationId === updatedSpec.SpecificationId
        )
        if (index !== -1) {
          state.dataSpecifications[index] = {
            ...state.dataSpecifications[index],
            Onboarding: updatedSpec.Onboarding,
          }
        }
      })
  },
})

export const { resetSpecifications } = specificationSlice.actions
export default specificationSlice.reducer
